<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Assinatura' : 'Nova Assinatura' }}
      </h1>
      <button @click="handleSubmit" class="btn btn-primary">
        Salvar
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label class="form-label">Streamer/Serviço</label>
          <select v-model="assinaturaModel.nomeStreamer" required class="form-select">
            <option value="" selected>Selecione</option>
            <option value="Outro">Outro / Não listado</option>
            <option v-for="platform in availablePlatforms" :key="platform" :value="platform">{{ platform }}</option>
          </select>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label class="form-label">Plataforma</label>
            <input v-model="assinaturaModel.plataforma" type="text" placeholder="Ex: Streaming" class="form-input" />
          </div>
          <div>
            <label class="form-label">Valor Mensal (R$)</label>
            <input v-model.number="assinaturaModel.valor" type="number" step="0.01" required placeholder="0,00" class="form-input" />
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label class="form-label">Data de Início</label>
            <input v-model="assinaturaModel.dataInicio" type="date" required class="form-input" />
          </div>
          <div>
            <label class="form-label">Próximo Vencimento</label>
            <input v-model="assinaturaModel.dataFim" type="date" required class="form-input" />
          </div>
        </div>
      </form>
    </div>

    <div v-if="loading" class="text-sm text-slate-500 dark:text-slate-400">Carregando...</div>
    <div v-if="error" class="text-sm text-rose-600 dark:text-rose-400 font-semibold">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const route = useRoute()

const isEditing = computed(() => !!route.params.id)

const getInitialModel = () => ({
  id: null,
  nomeStreamer: '',
  plataforma: '',
  valor: null,
  dataInicio: new Date().toISOString().split('T')[0],
  dataFim: ''
})
const assinaturaModel = ref(getInitialModel())

const apiUrl = 'https://backend.controlease.com.br/assinaturas-streamer'
const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

watch(() => assinaturaModel.value.dataInicio, (nova) => {
  if (!isEditing.value && nova) {
    const d = new Date(nova + 'T12:00:00')
    const next = new Date(d.setMonth(d.getMonth() + 1))
    assinaturaModel.value.dataFim = next.toISOString().split('T')[0]
  }
})

const platformStyles = {
  'controlease': {}, 'chatgpt': {}, 'google one': {}, 'microsoft 365': {}, 'adobe': {}, 'canva': {}, 'default': {},
  'netflix': {}, 'spotify': {}, 'twitch': {}, 'youtube': {}, 'prime video': {}, 'disney': {}, 'max': {},
  'crunchyroll': {}, 'paramount': {}, 'apple tv': {}, 'globoplay': {}, 'r7': {}, 'deezer': {}
}
const capitalize = (str) => !str ? '' : str.split(' ').map(w => (['tv+', 'one', '365'].includes(w.toLowerCase()) ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase())).join(' ')
const availablePlatforms = computed(() => Object.keys(platformStyles).filter(k => k !== 'default').map(capitalize).sort())

const toISODateInput = (dateString) => {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (isNaN(d.getTime())) return ''
  const yyyy = d.getUTCFullYear()
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const hydrateFromData = (data) => {
  assinaturaModel.value = {
    ...getInitialModel(),
    ...data,
    id: data.id ?? data?.uuid ?? null,
    valor: Number(data.valor ?? assinaturaModel.value.valor ?? 0),
    dataInicio: toISODateInput(data.dataInicio || data.inicio || assinaturaModel.value.dataInicio),
    dataFim: toISODateInput(data.dataFim || data.proximoVencimento || assinaturaModel.value.dataFim)
  }
}

const fetchById = async (id) => {
  try {
    const { data } = await axios.get(`${apiUrl}/${id}`, authConfig())
    hydrateFromData(data)
    return true
  } catch {
    return false
  }
}

const fetchFromListAndPick = async (id) => {
  try {
    const { data } = await axios.get(apiUrl, authConfig())
    const found = Array.isArray(data) ? data.find(s => String(s.id ?? s.uuid) === String(id)) : null
    if (found) {
      hydrateFromData(found)
      return true
    }
    return false
  } catch {
    return false
  }
}

const loading = ref(false)
const error = ref('')

const loadForEdit = async () => {
  loading.value = true
  error.value = ''
  const id = route.params.id

  const navStateItem = router.options.history?.state?.item || window.history.state?.item
  if (navStateItem && String(navStateItem.id ?? navStateItem.uuid) === String(id)) {
    hydrateFromData(navStateItem)
    loading.value = false
    return
  }

  const gotFromList = await fetchFromListAndPick(id)
  if (gotFromList) {
    loading.value = false
    return
  }

  const gotDirect = await fetchById(id)
  if (gotDirect) {
    loading.value = false
    return
  }

  error.value = 'Assinatura não encontrada.'
  loading.value = false
}

const handleSubmit = async () => {
  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${assinaturaModel.value.id}`, assinaturaModel.value, authConfig())
    } else {
      await axios.post(apiUrl, assinaturaModel.value, authConfig())
    }
    router.push('/user/assinaturas')
  } catch {
    alert('Não foi possível salvar a assinatura.')
  }
}

const goBack = () => {
  router.go(-1)
}

onMounted(() => {
  if (isEditing.value) loadForEdit()
})
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input,.form-select { @apply w-full px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-800/70 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] focus:bg-white dark:focus:bg-slate-900; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
</style>
